/**
 * Trial tab — opt-out trial health dashboard.
 *
 * Layout:
 *   [ Roll-up summary strip: in trial · subscribed · bailed · resubscribers ]
 *   [ 8 stage tiles in a row (responsive grid) ]
 *   [ 8 activation metric cards: stayed vs cancelled-in-trial ]
 *
 * Coach Cedar runs an opt-out trial — users auto-convert unless they actively
 * unsubscribe. This dashboard answers two operating questions a founder needs
 * to glance at any time of day:
 *   1. Where in the lifecycle is everyone right now?
 *   2. What does a "stayer" do during trial that a "bailer" doesn't?
 */
import React from 'react';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Hourglass,
  RotateCcw,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  PreTrialFunnelPayload,
  PreTrialFunnelStage,
  PreTrialFunnelWindow,
  TrialActivationMetric,
  TrialActivationMetricsPayload,
  TrialConversionCohort,
  TrialConversionPayload,
  TrialStageEntry,
  TrialStageGroup,
  TrialStagesPayload,
} from '../types';
import { ChartCard } from '../components/ChartCard';
import { KpiTile } from '../components/KpiTile';
import { cn } from '../../../../lib/cn';
import { formatFloat, formatInt, formatPercent } from '../format';

export interface TrialTabProps {
  range: AnalyticsRange;
}

// Install-date windows for the lifecycle funnel. Ordered widest → narrowest so
// the default (all-time) reads first. Mirrors the backend _FUNNEL_WINDOWS keys.
const FUNNEL_WINDOW_OPTIONS: Array<{ value: PreTrialFunnelWindow; label: string }> = [
  { value: 'all_time', label: 'All time' },
  { value: 'last_3_months', label: 'Last 3 mo' },
  { value: 'last_2_months', label: 'Last 2 mo' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_month', label: 'This month' },
];

export const TrialTab: React.FC<TrialTabProps> = ({ range }) => {
  const stages = useAnalyticsQuery<TrialStagesPayload>(
    ['trial-stages', range.queryString],
    `/api/admin/analytics/trial/stages?${range.queryString}`,
  );
  const activation = useAnalyticsQuery<TrialActivationMetricsPayload>(
    ['trial-activation-metrics', range.queryString],
    `/api/admin/analytics/trial/activation-metrics?${range.queryString}`,
  );
  // Conversion is all-time + fixed cohorts (not range-filtered), so no range key.
  const conversion = useAnalyticsQuery<TrialConversionPayload>(
    ['trial-conversion'],
    `/api/admin/analytics/trial/conversion`,
  );
  // Lifecycle funnel has its OWN install-date window selector (independent of
  // the page date filter), so it keys on `funnelWindow`, not `range`.
  const [funnelWindow, setFunnelWindow] = React.useState<PreTrialFunnelWindow>('all_time');
  const funnel = useAnalyticsQuery<PreTrialFunnelPayload>(
    ['pre-trial-funnel', funnelWindow],
    `/api/admin/analytics/trial/pre-trial-funnel?window=${funnelWindow}`,
  );

  const stagesPayload = stages.data?.data;
  const activationPayload = activation.data?.data;
  const conversionPayload = conversion.data?.data;
  const funnelPayload = funnel.data?.data;

  // Top KPI tiles always show CURRENT state (snapshot), independent of the
  // date filter — answers "what's happening right now?". The Trial Lifecycle
  // Distribution grid below uses `totals`/`stages` (cohort-scoped to the
  // selected date range, by signup_date) so the cohort lens is preserved
  // where it actually matters.
  const snapshot = stagesPayload?.snapshot;

  return (
    <div className="space-y-6">
      {/* Top roll-up summary strip — always reflects current platform state */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile
          label="Currently in trial"
          value={formatInt(snapshot?.in_trial)}
          icon={<Hourglass className="h-3 w-3" />}
          hint={
            snapshot && snapshot.pre_trial > 0
              ? `+${formatInt(snapshot.pre_trial)} signed up, trial not started · right now, regardless of date filter`
              : 'Right now, regardless of date filter'
          }
          loading={stages.isLoading}
        />
        <KpiTile
          label="Subscribed"
          value={formatInt(snapshot?.subscribed)}
          icon={<CheckCircle2 className="h-3 w-3" />}
          hint={
            snapshot && snapshot.resubscribers > 0
              ? `+${snapshot.resubscribers} returners`
              : 'Active paid subscribers right now'
          }
          loading={stages.isLoading}
        />
        <KpiTile
          label="Bailed (all-time)"
          value={formatInt(snapshot?.bailed)}
          icon={<XCircle className="h-3 w-3" />}
          hint="Cumulative cancels + lapsed"
          loading={stages.isLoading}
        />
        <KpiTile
          label="Resubscribers"
          value={formatInt(snapshot?.resubscribers)}
          icon={<RotateCcw className="h-3 w-3" />}
          hint="Cancelled then came back"
          loading={stages.isLoading}
        />
      </div>

      {/* Full lifecycle funnel: Installed → … → Reached gate → Started trial →
          Converted → Re-subscribed (month 2), with an install-date window. */}
      <ChartCard
        title="Lifecycle funnel"
        subtitle={
          funnelPayload?.definition ??
          'Installed → Gave name → Started onboarding (step 7) → Reached trial gate → Started the trial → Converted → Re-subscribed (month 2). Each stage is a share of Installed.'
        }
        loading={funnel.isLoading}
        error={funnel.error?.message ?? null}
        isEmpty={!funnelPayload?.stages?.length}
        onRetry={() => funnel.refetch()}
      >
        {/* Install-date window selector (independent of the page date filter). */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
            Install window
          </span>
          <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-0.5">
            {FUNNEL_WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFunnelWindow(opt.value)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  funnelWindow === opt.value
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {funnelPayload?.window_start && (
            <span className="text-[11px] text-gray-400">
              installed on/after {funnelPayload.window_start}
            </span>
          )}
        </div>

        <div className="space-y-2">
          {(funnelPayload?.stages ?? []).map((s, i) => (
            <PreTrialFunnelBar
              key={s.key}
              stage={s}
              prior={i > 0 ? funnelPayload!.stages[i - 1] : null}
            />
          ))}
        </div>

        {/* Stage-to-stage lifecycle conversion rates — the money metrics. */}
        {funnelPayload && (
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <FlowRateTile
              label="Gate → started trial"
              rate={funnelPayload.flow_rates.gate_to_trial}
            />
            <FlowRateTile
              label="Trial → paid (conversion)"
              rate={funnelPayload.flow_rates.trial_to_paid}
            />
            <FlowRateTile
              label="Paid → month 2 (retained)"
              rate={funnelPayload.flow_rates.paid_to_month2}
            />
          </div>
        )}

        {/* Drop-off callouts: declined at the gate + stalled before it. */}
        {funnelPayload && funnelPayload.gate_outcomes.declined.count > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-100">
            <XCircle className="h-3.5 w-3.5" />
            <span>
              <span className="font-semibold">{formatInt(funnelPayload.gate_outcomes.declined.count)}</span> declined at the gate
              {funnelPayload.gate_outcomes.declined.pct_of_reached != null
                ? ` (${formatPercent(funnelPayload.gate_outcomes.declined.pct_of_reached)} of reached)`
                : ''}{' '}
              — saw the paywall, never subscribed.
            </span>
          </div>
        )}
        {funnelPayload && funnelPayload.stalled.count > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-100">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>
              <span className="font-semibold">{formatInt(funnelPayload.stalled.count)}</span> stalled
              {funnelPayload.stalled.pct_of_installed != null
                ? ` (${formatPercent(funnelPayload.stalled.pct_of_installed)} of installed)`
                : ''}{' '}
              — entered the funnel, never reached the trial gate, and didn't return within 48h.
            </span>
          </div>
        )}
      </ChartCard>

      {/* Stage distribution — 8 boxes, COHORT view (filtered by signup date) */}
      <ChartCard
        title="Trial lifecycle distribution"
        subtitle="Of users who signed up in the selected date range, where are they now? Each user appears in exactly one tile. Trial stages are based on REAL trial dates (Start Free Trial), not signup age."
        loading={stages.isLoading}
        error={stages.error?.message ?? null}
        isEmpty={!stagesPayload?.stages?.length}
        onRetry={() => stages.refetch()}
      >
        {/* 8 tiles in a 4-up grid (2 rows) so labels have room — the prior
            9-col row made each tile too narrow and clipped the titles. */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {(stagesPayload?.stages ?? []).map((stage) => (
            <StageTile key={stage.key} stage={stage} />
          ))}
        </div>
        {stagesPayload && (
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-gray-400" /> Pre-trial (gate not tapped)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-blue-400" /> In trial
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> Subscribed
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-500" /> Bailed
            </span>
          </div>
        )}
      </ChartCard>

      {/* Trial → paid conversion — all-time + biweekly + monthly cohorts */}
      <ChartCard
        title="Trial → paid conversion"
        subtitle={
          conversionPayload?.definition ??
          'Of users who started a trial, the % who converted to a paid subscription, by trial-start cohort.'
        }
        loading={conversion.isLoading}
        error={conversion.error?.message ?? null}
        isEmpty={!conversionPayload}
        onRetry={() => conversion.refetch()}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold tabular-nums" style={{ color: 'var(--cedar-ink)' }}>
            {conversionPayload?.all_time.rate_pct != null
              ? formatPercent(conversionPayload.all_time.rate_pct)
              : 'N/A'}
          </span>
          <span className="text-xs text-gray-500">
            all-time ({conversionPayload?.all_time.converted ?? 0}/{conversionPayload?.all_time.starts ?? 0} converted)
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ConversionCohortTable title="Monthly cohorts" rows={conversionPayload?.monthly ?? []} />
          <ConversionCohortTable title="Biweekly cohorts" rows={conversionPayload?.biweekly ?? []} />
        </div>
      </ChartCard>

      {/* Activation metrics — stayed vs cancelled-in-trial */}
      <ChartCard
        title="What separates stayers from bailers"
        subtitle={
          activationPayload
            ? `Activation metrics over each user's first 7 days, split by outcome. ` +
              `Stayed cohort: ${activationPayload.cohort_sizes.stayed} users · ` +
              `Cancelled-in-trial cohort: ${activationPayload.cohort_sizes.cancelled_in_trial} users.`
            : 'Activation metrics over each user’s first 7 days, split by outcome.'
        }
        loading={activation.isLoading}
        error={activation.error?.message ?? null}
        isEmpty={!activationPayload?.metrics?.length}
        onRetry={() => activation.refetch()}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(activationPayload?.metrics ?? []).map((m) => (
            <ActivationMetricCard key={m.key} metric={m} />
          ))}
        </div>
        <p className="mt-4 text-[11px] text-gray-500">
          The metrics that diverge most between cohorts are your activation leading
          indicators &mdash; what to nudge users toward to reduce cancel-in-trial rate.
        </p>
      </ChartCard>
    </div>
  );
};

// ── Stage tile (one of 8) ──────────────────────────────────────────────────

interface StageTileProps {
  stage: TrialStageEntry;
}

const GROUP_TONE: Record<TrialStageGroup, { ring: string; accent: string; chip: string }> = {
  pre_trial: {
    ring: 'ring-gray-100',
    accent: 'text-gray-500',
    chip: 'bg-gray-50 text-gray-600 ring-gray-100',
  },
  in_trial: {
    ring: 'ring-blue-100',
    accent: 'text-blue-600',
    chip: 'bg-blue-50 text-blue-700 ring-blue-100',
  },
  subscribed: {
    ring: 'ring-emerald-100',
    accent: 'text-emerald-600',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
  bailed: {
    ring: 'ring-rose-100',
    accent: 'text-rose-600',
    chip: 'bg-rose-50 text-rose-700 ring-rose-100',
  },
};

const StageTile: React.FC<StageTileProps> = ({ stage }) => {
  const tone = GROUP_TONE[stage.group];
  return (
    <div
      className={cn(
        'group relative rounded-lg border border-gray-200 bg-white p-3 shadow-sm ring-1 transition-shadow hover:shadow-md',
        tone.ring,
      )}
    >
      <p className="text-[10.5px] font-semibold uppercase leading-tight tracking-wide text-gray-500">
        {stage.label}
      </p>
      <p className={cn('mt-1.5 text-2xl font-bold leading-none tabular-nums', tone.accent)}>
        {formatInt(stage.count)}
      </p>
      {stage.resubscribers > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1',
              tone.chip,
            )}
            title="Users in this stage who cancelled previously and came back"
          >
            <RotateCcw className="h-2.5 w-2.5" />
            {stage.resubscribers} returner{stage.resubscribers === 1 ? '' : 's'}
          </span>
          {stage.resub_type_a > 0 && (
            <span
              className="inline-flex items-center rounded-full bg-gray-50 px-1.5 py-0.5 text-[9.5px] font-medium text-gray-600 ring-1 ring-gray-200"
              title="Resubscribed within 14 days of original signup (≈7 days after trial end)"
            >
              {stage.resub_type_a} fast
            </span>
          )}
          {stage.resub_type_b > 0 && (
            <span
              className="inline-flex items-center rounded-full bg-gray-50 px-1.5 py-0.5 text-[9.5px] font-medium text-gray-600 ring-1 ring-gray-200"
              title="Resubscribed ≥30 days after their cancel — typically post-outreach"
            >
              {stage.resub_type_b} 30d+
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ── Conversion cohort table (monthly / biweekly) ───────────────────────────

const ConversionCohortTable: React.FC<{ title: string; rows: TrialConversionCohort[] }> = ({
  title,
  rows,
}) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</p>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400">No cohorts in range.</p>
    ) : (
      <table className="w-full text-xs tabular-nums">
        <thead>
          <tr className="text-left text-gray-400">
            <th className="pb-1 font-medium">Cohort</th>
            <th className="pb-1 text-right font-medium">Starts</th>
            <th className="pb-1 text-right font-medium">Converted</th>
            <th className="pb-1 text-right font-medium">Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.period} className="border-t border-gray-100">
              <td className="py-1 text-gray-700">{r.period}</td>
              <td className="py-1 text-right text-gray-700">{formatInt(r.starts)}</td>
              <td className="py-1 text-right text-gray-700">{formatInt(r.converted)}</td>
              <td className="py-1 text-right font-semibold text-gray-900">
                {r.rate_pct != null ? formatPercent(r.rate_pct) : 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

// ── Flow-rate tile (stage-to-stage lifecycle conversion) ───────────────────

const FlowRateTile: React.FC<{ label: string; rate: number | null }> = ({ label, rate }) => (
  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
    <p className="text-[10.5px] font-semibold uppercase leading-tight tracking-wide text-gray-500">
      {label}
    </p>
    <p className="mt-1 text-xl font-bold tabular-nums" style={{ color: 'var(--cedar-ink)' }}>
      {rate != null ? formatPercent(rate) : 'N/A'}
    </p>
  </div>
);

// ── Pre-trial funnel bar (one stage) ───────────────────────────────────────

const PreTrialFunnelBar: React.FC<{
  stage: PreTrialFunnelStage;
  prior: PreTrialFunnelStage | null;
}> = ({ stage, prior }) => {
  const pct = stage.pct_of_installed ?? 0;
  const dropFromPrior =
    prior && prior.count > 0 ? Math.round(100 * (1 - stage.count / prior.count)) : null;
  return (
    <div className="flex items-center gap-3">
      <div className="w-44 shrink-0 text-xs font-medium text-gray-700">{stage.label}</div>
      <div className="relative h-6 flex-1 overflow-hidden rounded bg-gray-100">
        <div className="h-full rounded bg-emerald-500" style={{ width: `${Math.max(pct, 1)}%` }} />
        <div className="absolute inset-0 flex items-center px-2 text-[11px] font-semibold text-gray-800">
          {formatInt(stage.count)}
          {stage.pct_of_installed != null ? ` · ${formatPercent(stage.pct_of_installed)}` : ''}
        </div>
      </div>
      <div className="w-16 shrink-0 text-right text-[11px] text-gray-400">
        {dropFromPrior != null && dropFromPrior > 0 ? `−${dropFromPrior}%` : ''}
      </div>
    </div>
  );
};

// ── Activation metric card (one of 8) ──────────────────────────────────────

interface ActivationMetricCardProps {
  metric: TrialActivationMetric;
}

function formatMetric(value: number | null, fmt: TrialActivationMetric['format']): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  if (fmt === 'percent') return formatPercent(value);
  if (fmt === 'days') return `${formatFloat(value, 1)}d`;
  return formatFloat(value, 1);
}

const ActivationMetricCard: React.FC<ActivationMetricCardProps> = ({ metric }) => {
  const stayed = formatMetric(metric.stayed, metric.format);
  const bailed = formatMetric(metric.cancelled_in_trial, metric.format);

  // Delta direction → colour. Stayed > bailed when delta > 0.
  let deltaTone: 'good' | 'bad' | 'neutral' = 'neutral';
  let DeltaIcon = TrendingUp;
  if (metric.delta != null && metric.lower_is_better != null) {
    const stayedHigher = metric.delta > 0.05;
    const stayedLower = metric.delta < -0.05;
    if (stayedHigher) {
      // Stayed cohort does this MORE — good if higher_is_better, bad if lower_is_better
      deltaTone = metric.lower_is_better ? 'bad' : 'good';
      DeltaIcon = TrendingUp;
    } else if (stayedLower) {
      deltaTone = metric.lower_is_better ? 'good' : 'bad';
      DeltaIcon = TrendingDown;
    }
  }

  const toneClass =
    deltaTone === 'good'
      ? 'text-emerald-700 bg-emerald-50 ring-emerald-100'
      : deltaTone === 'bad'
      ? 'text-rose-700 bg-rose-50 ring-rose-100'
      : 'text-gray-600 bg-gray-50 ring-gray-100';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <Calendar className="h-3 w-3 text-gray-400" />
          {metric.label}
        </p>
        {metric.delta != null && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ring-1',
              toneClass,
            )}
            title={
              deltaTone === 'good'
                ? 'Stayers do this more healthily than bailers'
                : deltaTone === 'bad'
                ? 'Bailers do this more healthily — investigate'
                : 'Cohorts behave similarly on this metric'
            }
          >
            <DeltaIcon className="h-3 w-3" />
            {metric.delta > 0 ? '+' : ''}
            {formatFloat(metric.delta, 1)}
          </span>
        )}
      </div>

      {/* Stayed / Bailed comparison */}
      <div className="mt-3 space-y-1.5">
        <div className="flex items-baseline justify-between text-[11px]">
          <span className="font-medium text-emerald-700">Stayed</span>
          <span className="font-semibold tabular-nums text-emerald-900">{stayed}</span>
        </div>
        <div className="flex items-baseline justify-between text-[11px]">
          <span className="font-medium text-rose-700">Cancelled in trial</span>
          <span className="font-semibold tabular-nums text-rose-900">{bailed}</span>
        </div>
      </div>

      {metric.delta == null && (
        <p className="mt-2 flex items-center gap-1 text-[10px] text-gray-400">
          <AlertCircle className="h-2.5 w-2.5" />
          Not enough data in one cohort yet
        </p>
      )}
    </div>
  );
};
